import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MapContainer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet-gpx';
import html2canvas from 'html2canvas';
import { useDropzone } from 'react-dropzone';
import { TileLayer } from 'react-leaflet';
import TextLegibilityControls from '../components/TextLegibilityControls';

// Add Google Fonts
const fontLink = document.createElement('link');
fontLink.href = 'https://fonts.googleapis.com/css2?family=Fredoka:wght@300;400;500;600;700&display=swap';
fontLink.rel = 'stylesheet';
document.head.appendChild(fontLink);

// Extend Leaflet types to include GPX
declare module 'leaflet' {
  interface GPX extends L.Layer {
    getBounds(): L.LatLngBounds;
    setStyle(style: L.PathOptions): this;
    get_distance(): number;
    get_elevation_gain(): number;
  }
  interface GPXOptions extends L.LayerOptions {
    async?: boolean;
    marker_options?: {
      startIconUrl?: string;
      endIconUrl?: string;
      shadowUrl?: string;
      wptIconUrls?: string | Object;
    };
  }
  interface GPXStatic {
    new(gpxData: string, options?: GPXOptions): GPX;
  }
  let GPX: GPXStatic;
}

const GPXMap = ({ 
  gpxData, 
  routeColor,
  routeWidth,
  zoomLevel
}: { 
  gpxData: string | null; 
  routeColor: string;
  routeWidth: number;
  zoomLevel: number;
}) => {
  const map = useMap();
  const gpxRef = useRef<L.GPX | null>(null);
  const initialZoomRef = useRef<number | null>(null);

  useEffect(() => {
    if (gpxData && !gpxRef.current) {
      // @ts-ignore - leaflet-gpx adds GPX constructor to L namespace
      gpxRef.current = new L.GPX(gpxData, {
        async: true,
        marker_options: {
          startIconUrl: '',
          endIconUrl: '',
          shadowUrl: ''
        }
      }).on('loaded', function(e: { target: L.GPX }) {
        map.fitBounds(e.target.getBounds());
        // Store the initial zoom level after fitting bounds
        initialZoomRef.current = map.getZoom();
        
        // Remove all markers from the map
        map.eachLayer((layer) => {
          if (layer instanceof L.Marker) {
            layer.remove();
          }
        });
      }).addTo(map);
    }

    return () => {
      if (gpxRef.current) {
        gpxRef.current.remove();
        gpxRef.current = null;
      }
    };
  }, [gpxData, map]);

  // Update zoom level when it changes
  useEffect(() => {
    if (initialZoomRef.current !== null) {
      const newZoom = initialZoomRef.current + zoomLevel;
      map.setZoom(newZoom);
    }
  }, [zoomLevel, map]);

  // Update route style when color or width changes
  useEffect(() => {
    if (gpxRef.current) {
      gpxRef.current.setStyle({
        color: routeColor,
        weight: routeWidth,
        opacity: 1,
      });
    }
  }, [routeColor, routeWidth]);

  return null;
};

function GpxExportPage() {
  const [gpxData, setGpxData] = useState<string | null>(null);
  const [routeColor, setRouteColor] = useState('#FF0000');
  const [routeWidth, setRouteWidth] = useState(3);
  const [distance, setDistance] = useState('0 km');
  const [elevation, setElevation] = useState('0 m');
  const [time, setTime] = useState('0:00:00');
  const [aspectRatio, setAspectRatio] = useState<'portrait' | 'landscape' | 'square'>('portrait');
  const [containerBorder, setContainerBorder] = useState(false);
  const [borderColor, setBorderColor] = useState('#000000');
  const [borderWidth, setBorderWidth] = useState(2);
  const [zoomLevel, setZoomLevel] = useState(0); // 0 means no adjustment from initial fit
  const [selectedFont, setSelectedFont] = useState('system-ui');
  const [overlayPosition, setOverlayPosition] = useState({ x: 20, y: 20 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const dragNodeRef = useRef<HTMLDivElement>(null);
  
  // Text styling states
  const [textStyles, setTextStyles] = useState<React.CSSProperties>({
    fontFamily: 'system-ui'
  });
  const [statsText, setStatsText] = useState({
    distance: 'Distance',
    elevation: 'Elevation Gain',
    time: 'Time'
  });

  const wrapperRef = useRef<HTMLDivElement>(null);
  const [filename, setFilename] = useState('route');

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/gpx+xml': ['.gpx']
    },
    maxFiles: 1,
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const gpxString = e.target?.result as string;
          setGpxData(gpxString);
          
          // Parse GPX data to extract stats
          const parser = new DOMParser();
          const gpxDoc = parser.parseFromString(gpxString, 'text/xml');
          
          // Extract distance and elevation from GPX
          const trkptElements = gpxDoc.getElementsByTagName('trkpt');
          let totalDistance = 0;
          let totalElevation = 0;
          let lastPoint: L.LatLng | null = null;
          let lastEle = 0;

          Array.from(trkptElements).forEach((pt, index) => {
            const lat = parseFloat(pt.getAttribute('lat') || '0');
            const lon = parseFloat(pt.getAttribute('lon') || '0');
            const ele = parseFloat(pt.getElementsByTagName('ele')[0]?.textContent || '0');
            
            if (lastPoint) {
              totalDistance += lastPoint.distanceTo([lat, lon]);
            }
            
            if (index > 0 && ele > lastEle) {
              totalElevation += ele - lastEle;
            }
            
            lastPoint = L.latLng(lat, lon);
            lastEle = ele;
          });

          setDistance(`${(totalDistance / 1000).toFixed(1)} km`);
          setElevation(`${Math.round(totalElevation)} m`);
        };
        reader.readAsText(file);
      }
    }
  });

  const handleExport = async () => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    try {
      console.log('Starting export...');
      console.log('Wrapper dimensions:', wrapper.offsetWidth, wrapper.offsetHeight);

      // Add a class to force a stacking context during export
      wrapper.classList.add('export-mode');

      // Calculate dimensions based on aspect ratio
      const baseWidth = aspectRatio === 'portrait' ? 1080 : aspectRatio === 'landscape' ? 1920 : 1080;
      const baseHeight = aspectRatio === 'portrait' ? 1920 : aspectRatio === 'landscape' ? 1080 : 1080;
      const scale = 2; // For high resolution

      const canvas = await html2canvas(wrapper, {
        width: baseWidth * scale,
        height: baseHeight * scale,
        scale: scale,
        backgroundColor: null,
        useCORS: true,
        logging: true,
        allowTaint: true,
        onclone: (clonedDoc: Document) => {
          const clonedWrapper = clonedDoc.getElementById('map-wrapper');
          if (clonedWrapper) {
            // Ensure the map container is visible and properly sized
            (clonedWrapper as HTMLElement).style.visibility = 'visible';
            (clonedWrapper as HTMLElement).style.opacity = '1';
            (clonedWrapper as HTMLElement).style.position = 'relative';
            (clonedWrapper as HTMLElement).style.width = `${baseWidth}px`;
            (clonedWrapper as HTMLElement).style.height = `${baseHeight}px`;
            
            // Force the map to render at the correct size
            const mapContainer = clonedWrapper.querySelector('.leaflet-container');
            if (mapContainer) {
              (mapContainer as HTMLElement).style.width = '100%';
              (mapContainer as HTMLElement).style.height = '100%';
              (mapContainer as HTMLElement).style.background = 'transparent';
            }
          }
        }
      } as any);

      console.log('Canvas dimensions:', canvas.width, canvas.height);

      // Create a new canvas with transparent background
      const finalCanvas = document.createElement('canvas');
      finalCanvas.width = baseWidth * scale;
      finalCanvas.height = baseHeight * scale;
      const ctx = finalCanvas.getContext('2d', { willReadFrequently: true });
      
      if (ctx) {
        // Clear the canvas with transparent background
        ctx.clearRect(0, 0, finalCanvas.width, finalCanvas.height);
        // Draw the original canvas content
        ctx.drawImage(canvas, 0, 0);
      }

      const link = document.createElement('a');
      link.download = `${filename}.png`;
      link.href = finalCanvas.toDataURL('image/png', 1.0);
      link.click();
    } catch (error) {
      console.error('Export error:', error);
    } finally {
      wrapper.classList.remove('export-mode');
    }
  };

  // Handle font changes separately from other style changes
  const handleFontChange = (font: string) => {
    setSelectedFont(font);
    setTextStyles(prev => ({
      ...prev,
      fontFamily: font,
      fontWeight: font === 'Fredoka' ? '500' : 'normal'
    }));
  };

  // Handle other style changes without affecting font
  const handleStyleChange = useCallback((newStyles: React.CSSProperties) => {
    setTextStyles(prev => ({
      ...newStyles,
      fontFamily: prev.fontFamily,
      fontWeight: prev.fontFamily === 'Fredoka' ? '500' : newStyles.fontWeight || 'normal'
    }));
  }, []); // Empty dependency array since we only use setState

  // Handle mouse down event to start dragging
  const handleMouseDown = (e: React.MouseEvent) => {
    if (dragNodeRef.current) {
      setIsDragging(true);
      dragStartPos.current = {
        x: e.clientX - overlayPosition.x,
        y: e.clientY - overlayPosition.y
      };
    }
  };

  // Handle mouse move event while dragging
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (isDragging && dragNodeRef.current) {
      const bounds = dragNodeRef.current.parentElement?.getBoundingClientRect();
      if (bounds) {
        const newX = e.clientX - dragStartPos.current.x;
        const newY = e.clientY - dragStartPos.current.y;
        
        // Constrain to parent bounds
        const x = Math.max(0, Math.min(newX, bounds.width - dragNodeRef.current.offsetWidth));
        const y = Math.max(0, Math.min(newY, bounds.height - dragNodeRef.current.offsetHeight));
        
        setOverlayPosition({ x, y });
      }
    }
  }, [isDragging]);

  // Handle mouse up event to stop dragging
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add and remove event listeners
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <div className="App flex h-screen">
      <div className="sidebar w-80 bg-gray-100 flex flex-col h-full">
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 pb-24 space-y-6">
            <h2 className="text-2xl font-bold text-gray-800">GPX Export Tool</h2>
            
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <input {...getInputProps()} />
              <p className="text-gray-600">
                {isDragActive
                  ? 'Drop the GPX file here'
                  : 'Drag and drop a GPX file here, or click to select'}
              </p>
            </div>

            {gpxData && (
              <div className="space-y-6">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Route Color
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="color"
                      value={routeColor}
                      onChange={(e) => setRouteColor(e.target.value)}
                      className="h-10 w-10 rounded cursor-pointer"
                    />
                    <span className="text-sm text-gray-600">
                      {routeColor}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Route Width
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={routeWidth}
                      onChange={(e) => setRouteWidth(Number(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <span className="text-sm text-gray-600 min-w-[2rem]">
                      {routeWidth}px
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Zoom Level
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="range"
                      min="-2"
                      max="4"
                      step="0.1"
                      value={zoomLevel}
                      onChange={(e) => setZoomLevel(Number(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <span className="text-sm text-gray-600 min-w-[2rem]">
                      {zoomLevel > 0 ? '+' : ''}{zoomLevel}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Aspect Ratio
                  </label>
                  <select
                    value={aspectRatio}
                    onChange={(e) => setAspectRatio(e.target.value as 'portrait' | 'landscape' | 'square')}
                    className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="portrait">Portrait (9:16)</option>
                    <option value="landscape">Landscape (16:9)</option>
                    <option value="square">Square (1:1)</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-gray-700">
                      Container Border
                    </label>
                    <input
                      type="checkbox"
                      checked={containerBorder}
                      onChange={(e) => setContainerBorder(e.target.checked)}
                      className="h-4 w-4 text-blue-600"
                    />
                  </div>
                  {containerBorder && (
                    <div className="space-y-2 pl-4">
                      <div className="flex items-center space-x-3">
                        <label className="block text-sm font-medium text-gray-700">
                          Border Color
                        </label>
                        <input
                          type="color"
                          value={borderColor}
                          onChange={(e) => setBorderColor(e.target.value)}
                          className="h-8 w-8 rounded cursor-pointer"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Border Width (px)
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="20"
                          value={borderWidth}
                          onChange={(e) => setBorderWidth(Number(e.target.value))}
                          className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Distance
                  </label>
                  <input
                    type="text"
                    value={distance}
                    onChange={(e) => setDistance(e.target.value)}
                    className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Elevation Gain
                  </label>
                  <input
                    type="text"
                    value={elevation}
                    onChange={(e) => setElevation(e.target.value)}
                    className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Time
                  </label>
                  <input
                    type="text"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Font Style
                  </label>
                  <select
                    value={selectedFont}
                    onChange={(e) => handleFontChange(e.target.value)}
                    className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="system-ui">System Default</option>
                    <option value="Fredoka">Bubble Font</option>
                    <option value="Arial">Arial</option>
                    <option value="Helvetica">Helvetica</option>
                  </select>
                </div>

                <TextLegibilityControls 
                  onStyleChange={handleStyleChange}
                  onTextChange={(text) => setStatsText(prev => ({ ...prev, distance: text }))}
                  initialText={statsText.distance}
                />
              </div>
            )}
          </div>
        </div>
        
        {/* Fixed button container */}
        {gpxData && (
          <div className="sticky bottom-0 p-6 bg-gray-100 border-t border-gray-200 shadow-inner space-y-3">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Export Filename
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={filename}
                  onChange={(e) => setFilename(e.target.value)}
                  placeholder="Enter filename"
                  className="flex-1 p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <span className="text-sm text-gray-500">.png</span>
              </div>
            </div>
            <button
              onClick={handleExport}
              disabled={!gpxData}
              className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                gpxData
                  ? 'bg-blue-500 text-white hover:bg-blue-600'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Download PNG
            </button>
          </div>
        )}
      </div>

      <main className="flex-1 p-6">
        <div 
          id="map-wrapper" 
          ref={wrapperRef}
          className={`relative mx-auto ${
            aspectRatio === 'portrait' ? 'w-[1080px] h-[1920px]' : 
            aspectRatio === 'landscape' ? 'w-[1920px] h-[1080px]' :
            'w-[1080px] h-[1080px]'
          }`}
          style={{
            transform: 'translate3d(0,0,0)',
            maxWidth: '100%',
            maxHeight: '90vh',
            border: containerBorder ? `${borderWidth}px solid ${borderColor}` : 'none',
            overflow: 'hidden' // Add this to prevent dragging outside bounds
          }}
        >
          <div className="absolute inset-0 bg-transparent">
            <MapContainer
              center={[0, 0]}
              zoom={2}
              style={{ 
                height: '100%', 
                width: '100%', 
                background: 'transparent',
                position: 'relative',
                zIndex: 1
              }}
              preferCanvas={true}
              zoomControl={false}
              attributionControl={false}
            >
              <GPXMap 
                gpxData={gpxData} 
                routeColor={routeColor} 
                routeWidth={routeWidth}
                zoomLevel={zoomLevel}
              />
            </MapContainer>
          </div>

          <div 
            ref={dragNodeRef}
            className="stats-overlay absolute p-4 rounded z-[9999] cursor-move"
            style={{ 
              transform: `translate(${overlayPosition.x}px, ${overlayPosition.y}px)`,
              position: 'absolute',
              zIndex: 2,
              userSelect: 'none',
              ...textStyles
            }}
            onMouseDown={handleMouseDown}
          >
            <div>
              <div>{statsText.distance}</div>
              <div>{distance}</div>
            </div>
            <div className="mt-2">
              <div>{statsText.elevation}</div>
              <div>{elevation}</div>
            </div>
            <div className="mt-2">
              <div>{statsText.time}</div>
              <div>{time}</div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default GpxExportPage; 